import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    Loader2, Search, Sparkles, HelpCircle, MessageSquareText,
    ArrowRight, X, MessageCircleQuestion, Wallet, FileText, Landmark,
    ScrollText, Users, ChevronDown, CheckCircle2, BadgeCheck, HandHeart,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ShareButton, appSharePayload, faqSharePayload } from "@/components/share/ShareButton";

/* ------------------------------------------------------------------ */
/* Tokenisation, stemming & highlight helpers — unchanged from before */
/* ------------------------------------------------------------------ */
const STOPWORDS = new Set([
    "il","lo","la","i","gli","le","un","uno","una","di","a","da","in","con","su","per","tra","fra",
    "e","o","ma","che","chi","cui","come","quando","dove","quale","quali","cosa","se","non",
    "mi","ti","ci","vi","ne","si","io","tu","egli","noi","voi","loro",
    "questo","questa","questi","queste","quello","quella","quelli","quelle",
    "posso","puoi","può","puo","possiamo","potete","possono",
    "sono","sei","è","e'","siamo","siete","ho","hai","ha","abbiamo","avete","hanno",
    "del","dello","della","dei","degli","delle","al","allo","alla","ai","agli","alle",
    "dal","dallo","dalla","dai","dagli","dalle","nel","nello","nella","nei","negli","nelle",
    "sul","sullo","sulla","sui","sugli","sulle",
]);
function tokenize(q) {
    return q.toLowerCase()
        .replace(/[^\p{L}\p{N}\s]+/gu, " ")
        .split(/\s+/)
        .filter(t => t.length >= 2 && !STOPWORDS.has(t));
}
function stem(t) { return t.length >= 5 ? t.replace(/[aeio]$/, "") : t; }
function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
function buildHighlightRe(tokens) {
    if (!tokens.length) return null;
    const patterns = tokens.map(t => t.length >= 5 ? `${escapeRe(t.slice(0, -1))}[aeio]?` : escapeRe(t))
        .sort((a, b) => b.length - a.length);
    try { return new RegExp(`(${patterns.join("|")})`, "gi"); } catch { return null; }
}
function Highlight({ text, re }) {
    if (!text) return null;
    if (!re) return <>{text}</>;
    const parts = text.split(re);
    return (
        <>
            {parts.map((p, i) => (i % 2 === 1
                ? <mark key={i} className="bg-accent/25 text-slate-900 rounded-sm px-0.5 py-px">{p}</mark>
                : <span key={i}>{p}</span>))}
        </>
    );
}
function makeSnippet(answer, tokens, radius = 100) {
    if (!answer) return "";
    if (!tokens.length) return answer.length > 220 ? answer.slice(0, 220) + "…" : answer;
    const low = answer.toLowerCase();
    let earliest = -1;
    for (const t of tokens) {
        const s = stem(t);
        const idx = low.indexOf(s);
        if (idx >= 0 && (earliest < 0 || idx < earliest)) earliest = idx;
    }
    if (earliest < 0) return answer.length > 220 ? answer.slice(0, 220) + "…" : answer;
    const start = Math.max(0, earliest - radius);
    const end = Math.min(answer.length, earliest + radius * 2);
    return (start > 0 ? "… " : "") + answer.slice(start, end) + (end < answer.length ? " …" : "");
}

/* Count-up hook — respects prefers-reduced-motion */
function useCountUp(end, duration = 900) {
    const [n, setN] = useState(end ?? 0);
    useEffect(() => {
        if (end == null) return;
        if (typeof window !== "undefined" && window.matchMedia
            && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setN(end); return;
        }
        let raf = 0; let start = null;
        const step = (t) => {
            if (!start) start = t;
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.floor(eased * end));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [end, duration]);
    return n;
}

/* Category icon lookup — heuristic on name, no schema changes */
function iconForCategory(name = "") {
    const n = name.toLowerCase();
    if (n.includes("assemble")) return FileText;
    if (n.includes("ripart") || n.includes("spes")) return Wallet;
    if (n.includes("rendiconto") || n.includes("bilancio")) return ScrollText;
    if (n.includes("amministr")) return Landmark;
    if (n.includes("condòmin") || n.includes("condomin")) return Users;
    return HelpCircle;
}

export default function Faqs() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState("all");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [searchDiag, setSearchDiag] = useState(null);
    const [searching, setSearching] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [catsOpen, setCatsOpen] = useState(false); // mobile-only collapse
    // Deep-link support: `/faq?faq=<id>` opens the specific FAQ.
    const [directFaqId, setDirectFaqId] = useState(() => {
        if (typeof window === "undefined") return null;
        try {
            return new URLSearchParams(window.location.search).get("faq") || null;
        } catch { return null; }
    });
    const [openAccordionValue, setOpenAccordionValue] = useState(undefined);
    // Feature flag: hide the "Sfoglia per argomento" browse section.
    // Set to `true` to restore the category grid + accordion list + filter.
    // Code and API calls remain intact — only rendering is suppressed.
    const SHOW_CATEGORIES_BROWSER = false;

    const INITIAL_VISIBLE = 2;
    const STEP = 5;
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

    useEffect(() => { API.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
    useEffect(() => {
        // "Prova una ricerca" — SORGENTE DATI VINCOLATA
        // Mostra ESCLUSIVAMENTE titoli di FAQ pubblicate (`FAQ.title`).
        // Non usa: risposta, domanda estesa, real_problem, alt_phrases, tag,
        // categoria, scoring, click learning, articoli, risorse, normative.
        // L'endpoint /public/faqs ritorna solo FAQ con status=published;
        // filtriamo comunque per la presenza di `title` non vuoto per
        // difenderci da eventuali contenuti non-FAQ o senza titolo.
        API.get("/public/faqs")
            .then(({ data }) => {
                const pool = (Array.isArray(data) ? data : [])
                    .filter(f => f && typeof f.title === "string" && f.title.trim())
                    .map(f => ({ text: f.title.trim(), faq_id: f.id }));
                // Fisher–Yates shuffle → 6 titoli casuali a ogni caricamento.
                for (let i = pool.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [pool[i], pool[j]] = [pool[j], pool[i]];
                }
                setSuggestions(pool.slice(0, 6));
            })
            .catch(() => setSuggestions([]));
    }, []);
    useEffect(() => {
        setLoading(true);
        const params = category !== "all" ? { category } : {};
        API.get("/public/faqs", { params }).then(({ data }) => setItems(data))
            .catch(e => setError(apiError(e))).finally(() => setLoading(false));
    }, [category]);
    useEffect(() => {
        const q = search.trim();
        const t = setTimeout(() => setDebouncedSearch(q.length >= 2 ? q : ""), 250);
        return () => clearTimeout(t);
    }, [search]);
    useEffect(() => {
        if (!debouncedSearch) { setSearchResults(null); setSearchDiag(null); return; }
        setSearching(true);
        const params = { q: debouncedSearch };
        if (category !== "all") params.category = category;
        API.get("/public/faqs/search", { params })
            .then(({ data }) => {
                setSearchResults(data.results || []);
                setSearchDiag({ tokens: data.tokens || [], corrected: data.corrected || [], fuzzy_map: data.fuzzy_map || {}, expanded: data.expanded || [] });
            })
            .catch(() => { setSearchResults([]); setSearchDiag(null); })
            .finally(() => setSearching(false));
    }, [debouncedSearch, category]);

    const query = search.trim();
    const tokens = useMemo(
        () => (searchDiag?.expanded && searchDiag.expanded.length ? searchDiag.expanded : tokenize(query)),
        [searchDiag, query]
    );
    const phrase = query.toLowerCase();
    const highlightRe = useMemo(() => buildHighlightRe(tokens), [tokens]);

    const isSearching = debouncedSearch.length > 0;
    const directFaq = useMemo(
        () => (directFaqId ? items.find(x => x.id === directFaqId) : null),
        [items, directFaqId]
    );
    // When a valid deep-link is present and the user is not actively
    // searching, render just the deep-linked FAQ in the same accordion.
    const showDirectFaq = Boolean(directFaq) && !isSearching;
    const filtered = isSearching
        ? (searchResults || [])
        : (showDirectFaq ? [directFaq] : items);

    // Auto-open + scroll to the deep-linked FAQ once items load
    useEffect(() => {
        if (!showDirectFaq) return;
        setOpenAccordionValue(directFaqId);
        const t = setTimeout(() => {
            const el = document.querySelector(`[data-testid="faq-${directFaqId}"]`);
            if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 250);
        return () => clearTimeout(t);
    }, [showDirectFaq, directFaqId]);

    // If the user starts searching (or manually clears the deep-link),
    // stop giving special treatment to the direct FAQ.
    useEffect(() => {
        if (isSearching && directFaqId) setDirectFaqId(null);
    }, [isSearching, directFaqId]);

    useEffect(() => { setVisibleCount(INITIAL_VISIBLE); }, [debouncedSearch, category, searchResults, isSearching]);
    const visibleResults = (isSearching || showDirectFaq) ? filtered.slice(0, visibleCount) : filtered;
    const remaining = Math.max(0, filtered.length - visibleCount);

    const trackClick = (faqId) => {
        if (!faqId) return;
        // Fire a "faq_view" analytics event on every accordion open (search or not).
        API.post("/public/faqs/view", { faq_id: faqId, q: debouncedSearch || null }).catch(() => {});
        // Additionally, when the user opened it from a search, log a "click"
        // for the ranking engine (existing behaviour).
        if (isSearching) {
            API.post("/public/faqs/search/click", { q: debouncedSearch, faq_id: faqId }).catch(() => {});
        }
    };

    const trackSuggestionClick = (text, faqId) => {
        if (!text) return;
        API.post("/public/suggestions/click", { text, faq_id: faqId || null }).catch(() => {});
    };

    // Group non-search items by category name for the compact browse-grid
    const groups = useMemo(() => filtered.reduce((acc, f) => {
        const key = f.category?.name || "Altro";
        (acc[key] = acc[key] || []).push(f);
        return acc;
    }, {}), [filtered]);
    const groupsList = Object.entries(groups);

    const totalFaqs = items.length;
    const animatedTotal = useCountUp(totalFaqs);

    return (
        <div className="relative">
            {/* Ambient gradient background */}
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[720px] bg-[radial-gradient(1200px_500px_at_50%_-10%,rgba(48,131,201,0.10),transparent_65%),radial-gradient(900px_400px_at_95%_5%,rgba(245,149,71,0.10),transparent_60%)]" />
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[720px] opacity-[0.025] [background-image:linear-gradient(rgba(48,131,201,1)_1px,transparent_1px),linear-gradient(90deg,rgba(48,131,201,1)_1px,transparent_1px)] [background-size:44px_44px]" />

            <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-16 pb-12">
                {/* ==================================================== */}
                {/* HERO                                                  */}
                {/* ==================================================== */}
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
                        <Sparkles className="size-3.5" /> Biblioteca CondominioNow
                    </div>
                    <h1
                        data-testid="faqs-title"
                        className="mt-5 font-serif text-4xl sm:text-5xl lg:text-[56px] font-semibold leading-[1.08] tracking-tight text-slate-900"
                    >
                        <span className="text-primary">Scrivi il tuo dubbio.</span>
                        <br />
                        Potresti trovare la risposta che cerchi in pochi secondi.
                    </h1>
                    <p
                        data-testid="faqs-subtitle"
                        className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto"
                    >
                        Scrivi il problema con parole tue. Nella maggior parte dei casi la risposta è già presente nella nostra biblioteca.
                    </p>
                </div>

                {/* ==================================================== */}
                {/* SEARCH CARD — dominant                                */}
                {/* ==================================================== */}
                <div
                    className="mt-8 sm:mt-10 rounded-[28px] border border-primary/15 bg-white shadow-[0_30px_80px_-30px_rgba(48,131,201,0.35),0_10px_25px_-15px_rgba(15,23,42,0.08)] p-5 sm:p-8 lg:p-10"
                >
                    {/* SEARCH INPUT — the true protagonist */}
                    <div className="group relative">
                        <div aria-hidden className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-primary/25 via-primary/5 to-accent/25 opacity-40 group-focus-within:opacity-100 blur transition" />
                        <div className="relative flex items-center gap-2 rounded-2xl border-2 border-slate-200 bg-white pl-4 pr-1.5 py-1.5 transition group-focus-within:border-primary group-focus-within:shadow-[0_10px_30px_-15px_rgba(48,131,201,0.5)]">
                            <Search className="size-5 sm:size-6 text-primary shrink-0" strokeWidth={2.2} />
                            <input
                                type="search"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Scrivi qui il tuo dubbio…"
                                aria-label="Scrivi qui il tuo dubbio"
                                data-testid="faqs-search"
                                autoComplete="off"
                                className="flex-1 min-w-0 h-12 sm:h-16 bg-transparent text-base sm:text-lg font-medium text-slate-900 placeholder:text-slate-400 outline-none border-0 focus:ring-0"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch("")}
                                    aria-label="Cancella ricerca"
                                    data-testid="faqs-search-clear"
                                    className="shrink-0 size-10 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center transition"
                                >
                                    <X className="size-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    <p
                        data-testid="faqs-search-hint"
                        className="mt-3 text-sm text-slate-600 leading-snug flex items-center gap-2 flex-wrap"
                    >
                        <Sparkles className="size-4 text-primary shrink-0" strokeWidth={2.2} />
                        <span>
                            {isSearching
                                ? (tokens.length ? "Cerchiamo su titolo, risposta, sinonimi e problema" : "Aggiungi una parola in più per cercare")
                                : "Ricerca in linguaggio naturale — scrivi come parleresti a una persona."}
                        </span>
                        {(loading || searching) && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                    </p>

                    {/* Pillole */}
                    {!isSearching && suggestions.length > 0 && (
                        <div className="mt-6" data-testid="faqs-suggestions">
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
                                Prova una ricerca
                            </div>
                            <div className="flex flex-wrap gap-2 sm:gap-2.5">
                                {suggestions.map((s, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => { trackSuggestionClick(s.text, s.faq_id); setSearch(s.text); }}
                                        data-testid={`faqs-suggestion-${i}`}
                                        className="group/pill inline-flex items-center gap-2 rounded-full border-2 border-primary/25 bg-white text-primary text-[14px] sm:text-[14.5px] font-semibold px-4 py-2.5 min-h-[44px] shadow-sm hover:bg-primary hover:text-white hover:border-primary hover:-translate-y-0.5 hover:shadow-[0_10px_20px_-10px_rgba(48,131,201,0.55)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/25 active:scale-[0.98] transition duration-200"
                                    >
                                        <Search className="size-3.5 opacity-70 group-hover/pill:opacity-100" />
                                        <span className="text-left">{s.text}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Secondary: authority chip + trust chips */}
                    <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap items-center gap-3 sm:gap-4">
                        <div
                            data-testid="faqs-authority"
                            className="inline-flex items-center gap-2 rounded-full bg-primary/[0.06] border border-primary/15 px-4 py-1.5"
                        >
                            <span className="text-[13px] text-slate-700 leading-tight">
                                Risposte curate dal <strong className="text-slate-900">Dr. Luigi Colaianni</strong>
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-[12.5px] text-slate-600">
                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircle2 className="size-3.5 text-primary" /> Sempre gratuita
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircle2 className="size-3.5 text-primary" /> Nessuna registrazione
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <BadgeCheck className="size-3.5 text-primary" /> Aggiornate ogni settimana
                            </span>
                        </div>
                    </div>
                </div>

                {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

                {/* ==================================================== */}
                {/* CATEGORY SELECT (small, secondary)                    */}
                {/* ==================================================== */}
                {SHOW_CATEGORIES_BROWSER && (
                    <div className="mt-6 flex items-center gap-2 flex-wrap">
                        <label htmlFor="faqs-category" className="text-xs text-muted-foreground">Filtra per categoria:</label>
                        <select
                            id="faqs-category"
                            value={category}
                            onChange={e => setCategory(e.target.value)}
                            className="h-9 rounded-full border border-slate-200 bg-white px-3 text-sm hover:border-primary/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
                            data-testid="faqs-category"
                        >
                            <option value="all">Tutte le categorie</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                )}

                {/* ==================================================== */}
                {/* RESULTS / BROWSE                                      */}
                {/* ==================================================== */}
                <div className="mt-8">
                    {(isSearching || showDirectFaq) ? (
                        filtered.length === 0 && !searching ? (
                            <Card className="border-dashed border-2 bg-slate-50/60" data-testid="faqs-empty">
                                <CardContent className="py-14 sm:py-16 text-center px-6">
                                    <div className="mx-auto size-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                                        <MessageCircleQuestion className="size-7" />
                                    </div>
                                    <h2 className="mt-4 font-serif text-2xl font-semibold text-slate-900">
                                        Non abbiamo trovato una risposta a questa domanda.
                                    </h2>
                                    <p className="mt-2 text-slate-600 max-w-md mx-auto">
                                        Non hai trovato quello che cercavi? Puoi inviare la tua domanda ai nostri esperti.
                                    </p>
                                    <Button asChild size="lg" data-testid="faqs-empty-cta" className="mt-6 bg-accent hover:bg-[#E17F2C] text-white font-semibold rounded-full">
                                        <Link to="/registrati">Fai una domanda <ArrowRight className="size-4 ml-1" /></Link>
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <>
                                <div className="flex items-center justify-between flex-wrap gap-2 mb-3 px-1">
                                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold" data-testid="faqs-results-count">
                                        {showDirectFaq
                                            ? <>Risposta condivisa</>
                                            : <>{filtered.length} risultat{filtered.length === 1 ? "o" : "i"} trovat{filtered.length === 1 ? "o" : "i"} per <span className="text-primary">"{query}"</span></>
                                        }
                                    </div>
                                </div>
                                {filtered.length > 0 && (
                                    <div
                                        data-testid="faqs-most-relevant"
                                        className="mt-1 mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-primary"
                                    >
                                        <Sparkles className="size-3.5" /> {showDirectFaq ? "Aperta dal link condiviso" : "Le risposte più pertinenti"}
                                    </div>
                                )}
                                <Accordion
                                    type="single"
                                    collapsible
                                    className="space-y-3"
                                    data-testid="faqs-results"
                                    value={openAccordionValue}
                                    onValueChange={(v) => { setOpenAccordionValue(v); trackClick(v); }}
                                >
                                    {visibleResults.map(f => (
                                        <AccordionItem
                                            key={f.id}
                                            value={f.id}
                                            data-testid={`faq-${f.id}`}
                                            className="rounded-2xl border border-primary/10 bg-white overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.03)] hover:shadow-[0_12px_28px_-18px_rgba(48,131,201,0.35)] hover:border-primary/30 transition duration-200"
                                        >
                                            <AccordionTrigger className="px-4 sm:px-6 py-5 hover:no-underline hover:bg-primary/[0.03] [&[data-state=open]]:bg-primary/[0.05] text-left">
                                                <div className="min-w-0 flex-1 flex items-start gap-3">
                                                    <div className="shrink-0 mt-0.5 size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                                                        <HelpCircle className="size-4" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        {f.category?.name && (
                                                            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">
                                                                <Highlight text={f.category.name} re={highlightRe} />
                                                            </div>
                                                        )}
                                                        <div className="font-semibold text-slate-900 leading-snug text-[15.5px]">
                                                            <Highlight text={f.title} re={highlightRe} />
                                                        </div>
                                                        <div className="mt-1.5 text-sm text-slate-600 leading-relaxed line-clamp-2">
                                                            <Highlight text={makeSnippet(f.answer, tokens)} re={highlightRe} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-4 sm:px-6 pb-6">
                                                <div className="pl-11 text-slate-700 leading-relaxed">
                                                    <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-widest mb-2.5">
                                                        <MessageSquareText className="size-3.5" /> Risposta del professionista
                                                    </div>
                                                    <p className="whitespace-pre-wrap">
                                                        <Highlight text={f.answer} re={highlightRe} />
                                                    </p>
                                                    <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                                                        <p className="flex-1 text-[13.5px] text-slate-600 leading-snug">
                                                            Conosci qualcuno con lo stesso problema?
                                                        </p>
                                                        <ShareButton
                                                            payload={faqSharePayload(f)}
                                                            variant="inline"
                                                            label="Condividi questa risposta"
                                                            data-testid={`faq-share-${f.id}`}
                                                        />
                                                    </div>
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                                {remaining > 0 && (
                                    <div className="mt-6 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={() => setVisibleCount(v => Math.min(v + STEP, filtered.length))}
                                            data-testid="faqs-show-more"
                                            aria-label={`Mostra altri ${Math.min(STEP, remaining)} risultati`}
                                            className="inline-flex items-center gap-2 rounded-full border-2 border-primary bg-white text-primary hover:bg-primary hover:text-white active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/25 font-semibold h-11 px-5 sm:px-6 text-[14.5px] shadow-[0_2px_6px_-3px_rgba(48,131,201,0.35)] transition min-h-[44px]"
                                        >
                                            Mostra altri {remaining} risultat{remaining === 1 ? "o" : "i"}
                                            <ArrowRight className="size-4" />
                                        </button>
                                    </div>
                                )}
                            </>
                        )
                    ) : SHOW_CATEGORIES_BROWSER ? (
                        filtered.length === 0 && !loading ? (
                            <Card className="border-dashed border-2 bg-slate-50/60" data-testid="faqs-empty">
                                <CardContent className="py-14 sm:py-16 text-center px-6">
                                    <div className="mx-auto size-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                                        <MessageCircleQuestion className="size-7" />
                                    </div>
                                    <h2 className="mt-4 font-serif text-2xl font-semibold text-slate-900">
                                        Ancora nessuna risposta veloce disponibile
                                    </h2>
                                    <p className="mt-2 text-slate-600 max-w-md mx-auto">
                                        Torna presto a trovarci.
                                    </p>
                                </CardContent>
                            </Card>
                        ) : loading ? (
                            <div className="text-center py-16"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                        ) : (
                            <>
                                {/* Compact browse-grid — secondary path */}
                                <div className="mt-2 mb-4 flex items-center justify-between gap-2">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                                            Sfoglia per argomento
                                        </div>
                                        <div className="mt-0.5 text-sm text-slate-500">
                                            Oppure scegli un argomento per esplorare
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setCatsOpen(v => !v)}
                                        aria-expanded={catsOpen}
                                        aria-controls="faqs-categories-grid"
                                        className="md:hidden inline-flex items-center gap-1.5 rounded-full border border-primary/25 text-primary text-xs font-semibold px-3 py-2 hover:bg-primary/10 transition min-h-[36px]"
                                    >
                                        {catsOpen ? "Nascondi" : "Mostra"}
                                        <ChevronDown className={`size-3.5 transition-transform ${catsOpen ? "rotate-180" : ""}`} />
                                    </button>
                                </div>

                                <div
                                    id="faqs-categories-grid"
                                    className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 ${catsOpen ? "grid" : "hidden"} md:grid`}
                                    data-testid="faqs-categories-grid"
                                >
                                    {groupsList.map(([catName, list]) => {
                                        const Icon = iconForCategory(catName);
                                        return (
                                            <button
                                                key={catName}
                                                type="button"
                                                onClick={() => {
                                                    const doc = document.getElementById(`cat-panel-${catName}`);
                                                    if (doc) doc.scrollIntoView({ behavior: "smooth", block: "start" });
                                                }}
                                                data-testid={`faqs-category-card-${catName}`}
                                                className="group text-left rounded-2xl border border-primary/10 bg-white p-4 hover:border-primary/40 hover:shadow-[0_10px_24px_-16px_rgba(48,131,201,0.35)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20 transition duration-200 min-h-[104px] flex flex-col"
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="size-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                                        <Icon className="size-4" />
                                                    </div>
                                                    <span className="inline-flex items-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold px-2 py-0.5">
                                                        {list.length}
                                                    </span>
                                                </div>
                                                <div className="mt-3 text-[10px] uppercase tracking-widest text-primary font-semibold">
                                                    {catName}
                                                </div>
                                                <div className="mt-auto pt-2 text-xs text-slate-500 group-hover:text-primary transition inline-flex items-center gap-1">
                                                    Sfoglia <ArrowRight className="size-3" />
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Existing accordion list — anchor targets for the cards */}
                                <Accordion type="single" collapsible className="mt-6 space-y-3" data-testid="faqs-list">
                                    {groupsList.map(([catName, list]) => (
                                        <AccordionItem
                                            key={catName}
                                            value={catName}
                                            id={`cat-panel-${catName}`}
                                            data-testid={`faqs-group-${catName}`}
                                            className="rounded-2xl border border-primary/10 bg-white overflow-hidden"
                                        >
                                            <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-primary/[0.03] [&[data-state=open]]:bg-primary/[0.05]">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs uppercase tracking-widest text-primary font-semibold">{catName}</span>
                                                    <span className="text-xs text-muted-foreground font-normal normal-case tracking-normal">
                                                        {list.length} {list.length === 1 ? "domanda" : "domande"}
                                                    </span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-3 sm:px-5 pb-4">
                                                <Accordion type="single" collapsible className="border-t border-slate-100">
                                                    {list.map(f => (
                                                        <AccordionItem key={f.id} value={f.id} className="px-2" data-testid={`faq-${f.id}`}>
                                                            <AccordionTrigger className="text-left font-semibold hover:no-underline">
                                                                <span className="flex items-start gap-2">
                                                                    <HelpCircle className="size-4 text-primary shrink-0 mt-0.5" />
                                                                    <span>{f.title}</span>
                                                                </span>
                                                            </AccordionTrigger>
                                                            <AccordionContent>
                                                                <div className="pl-6 text-slate-700 leading-relaxed">
                                                                    <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-widest mb-2">
                                                                        <MessageSquareText className="size-3.5" /> Risposta
                                                                    </div>
                                                                    <p className="whitespace-pre-wrap">{f.answer}</p>
                                                                </div>
                                                            </AccordionContent>
                                                        </AccordionItem>
                                                    ))}
                                                </Accordion>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </>
                        )
                    ) : null}
                </div>

                {/* ==================================================== */}
                {/* CTA finale — sfondo blu, bottone arancio               */}
                {/* ==================================================== */}
                <div className="mt-14 relative overflow-hidden rounded-[28px] bg-primary text-white p-8 sm:p-12 text-center">
                    <div aria-hidden className="absolute inset-0 bg-[radial-gradient(700px_260px_at_0%_0%,rgba(245,149,71,0.30),transparent_60%),radial-gradient(600px_260px_at_100%_100%,rgba(255,255,255,0.10),transparent_60%)]" />
                    <div className="relative">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-accent">
                            <BadgeCheck className="size-3.5" /> Servizio professionale
                        </div>
                        <h3 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight">
                            Non hai trovato la tua risposta?
                        </h3>
                        <p className="mt-3 text-white/90 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
                            Invia una domanda personalizzata: un professionista reale ti risponderà direttamente e in modo privato.
                        </p>
                        <div className="mt-7">
                            <Button
                                asChild
                                size="lg"
                                className="bg-accent hover:bg-[#E17F2C] text-white font-semibold rounded-full h-12 px-7 shadow-[0_6px_16px_-8px_rgba(245,149,71,0.7)] transition"
                            >
                                <Link to="/registrati">Fai una domanda <ArrowRight className="size-4 ml-1" /></Link>
                            </Button>
                        </div>
                    </div>
                </div>

                {/* ==================================================== */}
                {/* PEER-HELP SHARE CTA — prominent, on-brand             */}
                {/* ==================================================== */}
                <div
                    className="mt-8 sm:mt-10 relative overflow-hidden rounded-3xl bg-primary text-white px-6 sm:px-10 py-7 sm:py-9 shadow-[0_20px_45px_-20px_rgba(48,131,201,0.55)]"
                    data-testid="faqs-share-app-block"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_300px_at_100%_0%,rgba(245,149,71,0.35),transparent_60%),radial-gradient(500px_200px_at_0%_100%,rgba(255,255,255,0.10),transparent_60%)]"
                    />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">
                        <div className="shrink-0 hidden sm:flex size-14 rounded-2xl bg-accent text-white items-center justify-center shadow-[0_8px_18px_-8px_rgba(245,149,71,0.7)]">
                            <HandHeart className="size-7" strokeWidth={2.2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 border border-accent/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                                <HandHeart className="size-3.5 sm:hidden" /> Aiuta chi ne ha bisogno
                            </div>
                            <p className="mt-3 font-serif text-xl sm:text-2xl leading-snug font-semibold text-white">
                                Conosci qualcuno con un problema condominiale?
                            </p>
                            <p className="mt-1.5 text-[15px] sm:text-base text-white/90 leading-relaxed">
                                Condividi CondominioNow e aiutalo a trovare la risposta che cerca.
                            </p>
                        </div>
                        <div className="shrink-0 w-full sm:w-auto">
                            <ShareButton
                                payload={appSharePayload()}
                                variant="accent-lg"
                                label="Condividi CondominioNow"
                                data-testid="faqs-share-app"
                                className="w-full sm:w-auto"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
